import { Cctv, BellRing, KeyRound, Flame, Fingerprint, Home, Building2, Factory, Check } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { useLang } from "@/i18n";
import { SERVICE_KEYS, SLOTS, LEGAL_ROUTES } from "@/content";

const SVC_ICON = { cctv: Cctv, alarm: BellRing, access: KeyRound, fire: Flame, time: Fingerprint };
const PROP_ICON = { residential: Home, commercial: Building2, industrial: Factory };

const Q = ({ title, hint }) => (
  <div className="mb-6">
    <h3 className="font-display font-bold uppercase text-2xl sm:text-3xl text-white">{title}</h3>
    {hint && <p className="mt-1 text-sm text-zinc-400">{hint}</p>}
  </div>
);

const Tile = ({ active, onClick, testId, children }) => (
  <button type="button" data-testid={testId} onClick={onClick} aria-pressed={active}
    className={`relative text-left p-5 border transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${active ? "border-red-600 bg-red-950/40 red-glow" : "border-white/10 bg-white/[0.02] hover:border-white/30"}`}>
    {active && <Check className="absolute top-3 right-3 h-4 w-4 text-red-500" />}
    {children}
  </button>
);

export const StepSystems = ({ form, set }) => {
  const { t } = useLang();
  const toggle = (k) => set({ services: form.services.includes(k) ? form.services.filter((s) => s !== k) : [...form.services, k] });
  return (
    <div>
      <Q title={t.booking.q1} hint={t.booking.q1hint} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {SERVICE_KEYS.map((k) => {
          const Icon = SVC_ICON[k];
          return (
            <Tile key={k} testId={`booking-service-${k}`} active={form.services.includes(k)} onClick={() => toggle(k)}>
              <Icon className="h-6 w-6 text-red-500" strokeWidth={1.5} />
              <p className="mt-4 font-display font-bold uppercase text-lg text-white">{t.services.items[k].title}</p>
            </Tile>
          );
        })}
      </div>
    </div>
  );
};

export const StepProperty = ({ form, set }) => {
  const { t } = useLang();
  const b = t.booking;
  return (
    <div>
      <Q title={b.q2} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(b.property).map(([k, [name, desc]]) => {
          const Icon = PROP_ICON[k];
          return (
            <Tile key={k} testId={`booking-property-${k}`} active={form.property_type === k} onClick={() => set({ property_type: k })}>
              <Icon className="h-6 w-6 text-red-500" strokeWidth={1.5} />
              <p className="mt-4 font-display font-bold uppercase text-lg text-white">{name}</p>
              <p className="text-xs text-zinc-400 mt-1">{desc}</p>
            </Tile>
          );
        })}
      </div>
      <p className="mt-8 mb-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{b.areaLabel}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {Object.entries(b.areas).map(([k, label]) => (
          <button key={k} type="button" data-testid={`booking-area-${k}`} onClick={() => set({ area: k })}
            className={`py-3 px-3 border font-mono text-xs transition-colors ${form.area === k ? "border-red-600 bg-red-600 text-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>{label}</button>
        ))}
      </div>
    </div>
  );
};

export const StepDate = ({ form, set, locale, minDate, maxDate }) => {
  const { t } = useLang();
  return (
    <div>
      <Q title={t.booking.q3} />
      <div className="grid md:grid-cols-2 gap-8">
        <div className="border border-white/10 bg-white/[0.02] w-fit max-w-full" data-testid="booking-calendar">
          <Calendar mode="single" locale={locale} weekStartsOn={1} selected={form.date} onSelect={(d) => set({ date: d })}
            disabled={[{ before: minDate }, { after: maxDate }, { dayOfWeek: [0] }]} fromDate={minDate} toDate={maxDate}
            classNames={{ head_cell: "text-zinc-400 w-10 font-mono font-normal text-xs", day: "h-10 w-10 p-0 font-mono text-sm rounded-none hover:bg-white/10 transition-colors aria-selected:opacity-100", caption_label: "font-display uppercase tracking-wider text-lg" }} />
        </div>
        <div>
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{t.booking.slotLabel}</p>
          <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
            {SLOTS.map((s) => (
              <button key={s} type="button" data-testid={`booking-slot-${s.slice(0, 2)}`} onClick={() => set({ slot: s })}
                className={`py-3.5 px-4 border font-mono text-sm text-left transition-colors ${form.slot === s ? "border-red-600 bg-red-600 text-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>{s.replace("-", " — ")}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, testId, className = "", ...props }) => (
  <label className={`block ${className}`}>
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
    <input data-testid={testId} {...props} className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-red-500 outline-none py-2.5 text-white placeholder:text-zinc-600 transition-colors" />
  </label>
);

export const StepContact = ({ form, set }) => {
  const { t } = useLang();
  const b = t.booking;
  return (
    <div>
      <Q title={b.q4} />
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-6">
        <Field label={b.name} testId="booking-input-name" value={form.name} maxLength={80} autoComplete="name" onChange={(e) => set({ name: e.target.value })} />
        <Field label={b.phone} testId="booking-input-phone" type="tel" value={form.phone} maxLength={20} autoComplete="tel" onChange={(e) => set({ phone: e.target.value })} />
        <Field label={b.email} testId="booking-input-email" type="email" value={form.email} maxLength={120} autoComplete="email" onChange={(e) => set({ email: e.target.value })} />
        <Field label={b.city} testId="booking-input-city" value={form.city} maxLength={60} onChange={(e) => set({ city: e.target.value })} />
        <Field label={b.address} testId="booking-input-address" className="sm:col-span-2" value={form.address} maxLength={160} onChange={(e) => set({ address: e.target.value })} />
        <label className="block sm:col-span-2">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{b.notes}</span>
          <textarea data-testid="booking-input-notes" rows={3} maxLength={1000} value={form.notes} onChange={(e) => set({ notes: e.target.value })} className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-red-500 outline-none py-2.5 text-white resize-none transition-colors" />
        </label>
        <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer">
          <Checkbox data-testid="booking-consent-checkbox" checked={form.consent} onCheckedChange={(v) => set({ consent: v === true })} className="mt-0.5 rounded-none border-white/40 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
          <span className="text-sm text-zinc-300">{b.consent} <a href={LEGAL_ROUTES.privacy} target="_blank" rel="noreferrer" data-testid="booking-privacy-link" onClick={(e) => e.stopPropagation()} className="text-white underline underline-offset-4 decoration-red-600">{b.consentLink}</a></span>
        </label>
      </div>
    </div>
  );
};
